import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash, FaDownload, FaSearch } from 'react-icons/fa';

const emptyClient = {
  name: '',
  email: '',
  phone: '',
  status: 'Active',
  notes: '',
  lastContact: new Date().toISOString().split('T')[0],
  source: 'Direct',
  tags: []
};

const statusColors = {
  Active: 'bg-green-100 text-green-800',
  Inactive: 'bg-gray-100 text-gray-800',
  Prospect: 'bg-blue-100 text-blue-800',
  'On Hold': 'bg-yellow-100 text-yellow-800'
};

const CRM = () => {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(emptyClient);
  const [editingIndex, setEditingIndex] = useState(null);
  const [search, setSearch] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchClients = async () => {
      setIsLoading(true);
      setError('');
      try {
        let apiUrl = '/api/get_contacts.php';
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          const port = window.location.port;
          apiUrl = `http://${window.location.hostname}${port ? ':' + port : ''}/api/get_contacts.php`;
        }

        const response = await fetch(apiUrl, { credentials: 'include' });
        const result = await response.json();

        if (result.success) {
          const formattedData = result.data.map(contact => ({
            id: contact.id,
            name: contact.name,
            email: contact.email,
            message: contact.message,
            createdAt: contact.created_at,
            phone: '',
            status: 'Active',
            lastContact: new Date(contact.created_at).toISOString().split('T')[0],
            source: 'Website',
            tags: ['Contact Form'],
            notes: contact.message
          }));
          
          setClients(formattedData);
        } else {
          throw new Error(result.error || 'Failed to fetch client data');
        }
      } catch (err) {
        console.error('Error fetching clients:', err);
        setError('Failed to load client data. Please try again later.');
        
        const data = JSON.parse(localStorage.getItem('crmClients') || '[]');
        if (data.length > 0) {
          setClients(data);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchClients();
  }, []);

  useEffect(() => {
    localStorage.setItem('crmClients', JSON.stringify(clients));
  }, [clients]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      alert('Please enter a valid email address');
      return;
    }

    if (editingIndex !== null) {
      const updated = [...clients];
      updated[editingIndex] = { ...form, lastContact: new Date().toISOString().split('T')[0] };
      setClients(updated);
      setEditingIndex(null);
    } else {
      setClients([{ ...form, lastContact: new Date().toISOString().split('T')[0] }, ...clients]);
    }
    setForm(emptyClient);
  };

  const handleEdit = (idx) => {
    setForm(clients[idx]);
    setEditingIndex(idx);
  };

  const handleDelete = (idx) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      setClients(clients.filter((_, i) => i !== idx));
      if (editingIndex === idx) setForm(emptyClient);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      setForm({ ...form, tags: [...form.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setForm({ ...form, tags: form.tags.filter(tag => tag !== tagToRemove) });
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Status', 'Notes', 'Last Contact', 'Source', 'Tags'];
    const csvContent = [
      headers.join(','),
      ...clients.map(client => [
        client.name,
        client.email,
        client.phone,
        client.status,
        client.notes,
        client.lastContact,
        client.source,
        client.tags.join(';')
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `crm_clients_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const filteredClients = clients.filter(c => {
    const matchesSearch = 
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase()) ||
      c.phone.toLowerCase().includes(search.toLowerCase()) ||
      c.tags.some(tag => tag.toLowerCase().includes(search.toLowerCase()));
    
    const matchesStatus = filterStatus === 'All' || c.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="max-w-7xl mx-auto my-12 p-8 bg-white rounded-xl shadow-lg">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-purple-700">CRM Client Management</h2>
        <button
          onClick={exportToCSV}
          disabled={isLoading || clients.length === 0}
          className={`flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition ${(isLoading || clients.length === 0) ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <FaDownload /> Export CSV
        </button>
      </div>
      
      {error && (
        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700">
          <p className="font-medium">Error</p>
          <p>{error}</p>
        </div>
      )}
      
      {isLoading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <input
          type="text"
          name="name"
          value={form.name}
          onChange={handleChange}
          placeholder="Name"
          className="px-3 py-2 border rounded"
          required
        />
        <input
          type="email"
          name="email"
          value={form.email}
          onChange={handleChange}
          placeholder="Email"
          className="px-3 py-2 border rounded"
          required
        />
        <input
          type="tel"
          name="phone"
          value={form.phone}
          onChange={handleChange}
          placeholder="Phone"
          className="px-3 py-2 border rounded"
        />
        <select
          name="status"
          value={form.status}
          onChange={handleChange}
          className="px-3 py-2 border rounded"
        >
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
          <option value="Prospect">Prospect</option>
          <option value="On Hold">On Hold</option>
        </select>
        <select
          name="source"
          value={form.source}
          onChange={handleChange}
          className="px-3 py-2 border rounded"
        >
          <option value="Direct">Direct</option>
          <option value="Referral">Referral</option>
          <option value="Website">Website</option>
          <option value="Social Media">Social Media</option>
        </select>
        <div className="flex gap-2">
          <input
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            placeholder="Add tags..."
            className="px-3 py-2 border rounded flex-1"
          />
          <button
            type="button"
            onClick={handleAddTag}
            className="bg-purple-600 text-white px-3 py-2 rounded hover:bg-purple-700"
          >
            Add
          </button>
        </div>
        <textarea
          name="notes"
          value={form.notes}
          onChange={handleChange}
          placeholder="Notes"
          className="px-3 py-2 border rounded"
          rows="3"
        />
        <div className="flex flex-wrap gap-2">
          {form.tags.map((tag, index) => (
            <span
              key={index}
              className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-sm flex items-center gap-1"
            >
              {tag}
              <button
                type="button"
                onClick={() => handleRemoveTag(tag)}
                aria-label={`Remove tag ${tag}`}
                className="text-purple-600 hover:text-purple-800"
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <button
          type="submit"
          className="col-span-full bg-purple-700 hover:bg-purple-800 text-white font-bold py-2 rounded transition"
        >
          {editingIndex !== null ? 'Update Client' : 'Add Client'}
        </button>
      </form>

      <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex gap-4 items-center">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search clients..."
              className="pl-10 pr-4 py-2 border rounded w-full md:w-64"
            />
          </div>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="px-3 py-2 border rounded"
          >
            <option value="All">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Prospect">Prospect</option>
            <option value="On Hold">On Hold</option>
          </select>
        </div>
        <div className="text-gray-500">Total: {clients.length} clients</div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full border border-gray-200">
          <thead>
            <tr className="bg-purple-100">
              <th className="py-2 px-4 border">Name</th>
              <th className="py-2 px-4 border">Email</th>
              <th className="py-2 px-4 border">Phone</th>
              <th className="py-2 px-4 border">Status</th>
              <th className="py-2 px-4 border">Source</th>
              <th className="py-2 px-4 border">Last Contact</th>
              <th className="py-2 px-4 border">Tags</th>
              <th className="py-2 px-4 border">Notes</th>
              <th className="py-2 px-4 border">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredClients.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center py-4 text-gray-500">
                  No clients found.
                </td>
              </tr>
            ) : (
              filteredClients.map((c, i) => (
                <tr key={i} className="hover:bg-purple-50">
                  <td className="py-2 px-4 border font-semibold">{c.name}</td>
                  <td className="py-2 px-4 border">{c.email}</td>
                  <td className="py-2 px-4 border">{c.phone}</td>
                  <td className="py-2 px-4 border">
                    <span className={`px-2 py-1 rounded-full text-sm ${statusColors[c.status]}`}>
                      {c.status}
                    </span>
                  </td>
                  <td className="py-2 px-4 border">{c.source}</td>
                  <td className="py-2 px-4 border">{c.lastContact}</td>
                  <td className="py-2 px-4 border">
                    <div className="flex flex-wrap gap-1">
                      {c.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-2 px-4 border max-w-xs truncate">{c.notes}</td>
                  <td className="py-2 px-4 border">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(clients.indexOf(c))}
                        className="text-blue-600 hover:text-blue-800"
                        aria-label={`Edit ${c.name}`}
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDelete(clients.indexOf(c))}
                        className="text-red-600 hover:text-red-800"
                        aria-label={`Delete ${c.name}`}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CRM; 